import React, { useState, useEffect, useRef } from 'react';

import { Box } from '@chakra-ui/react';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import QuestionType from 'screens/admin/Reviews/ReviewQuestionBankV1/components/QuestionType';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { GENDER_MAPPING, QUESTION_TYPES } from 'constants/ProjectConstants';

import { AgGridSearchBar } from 'uiToolkitV1/AgGridSearchBar';
import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { PaperWithShadow } from 'uiToolkitV1/Paper';

import { getColumnDefinitionByType } from '../TalentPlanning/columnDefinitions';
import { useGetReviewsDetailedReport } from './dataHooks';

// this should not be there in api response at all, but are there for some technical reason.
// if removed from BE, this filter can also be removed.
// discuss with Arun for details
export const filterNotRequiredColumns = (col) =>
  ![
    'EMAIL',
    'PERSONAL_EMAIL',
    'WORK_EMAIL',
    'MANAGER_EMAIL',
    'MANAGER_WORK_EMAIL',
    'MANAGER_PERSONAL_EMAIL',
    'PROFILE_PHOTO',
  ].includes(col.columnId);

function ReviewDetailedReportGrid({ gridRef, gridStatePersistenceKey }) {
  const currentCompRef = useRef();
  const agGridRef = gridRef ?? currentCompRef;

  const [columnDefs, setColumnDefs] = useState([]);

  const { isFetching, data: reviewsDetailedReportsData } = useGetReviewsDetailedReport();
  const { columns, rows = [] } = reviewsDetailedReportsData ?? {};

  useEffect(() => {
    if (!columns || columns.length === 0) return;
    const newColumnDefs = columns.filter(filterNotRequiredColumns).map((col) => {
      const additionalProps: {
        valueGetter?: (params: unknown) => unknown;
        valueFormatter?: (params: unknown) => unknown;
        cellRenderer?: React.ComponentType<unknown>;
        cellRendererParams?: Record<string, unknown>;
        filter?: string;
        filterParams?: Record<string, unknown>;
      } = {};

      if (col.columnId === 'QUESTION_TYPE') {
        additionalProps.valueGetter = (params: { data?: { QUESTION_TYPE?: string } }) => params.data?.QUESTION_TYPE;
        additionalProps.valueFormatter = (params: { data?: { QUESTION_TYPE?: string } }) => params.data?.QUESTION_TYPE;
        additionalProps.cellRenderer = QuestionType;
        additionalProps.cellRendererParams = { keyName: 'QUESTION_TYPE' };
        additionalProps.filter = 'agSetColumnFilter';
        additionalProps.filterParams = {
          valueFormatter: (params) =>
            QUESTION_TYPES.review.find((item) => item.value === params.value)?.label || params.value,
        };
      } else if (col.columnId === 'GENDER') {
        additionalProps.valueGetter = (params: { data?: { GENDER?: string } }) =>
          GENDER_MAPPING[params.data?.GENDER] || params.data?.GENDER;
        additionalProps.cellRendererParams = { isValueGetter: true };
      }

      const colProps = col;
      return {
        colId: colProps.columnId,
        field: colProps.columnId,
        id: colProps.columnId,
        displayName: colProps.displayName,
        description: colProps.description,
        minWidth: 220,
        headerComponent: CustomHeaderCell,
        headerComponentParams: {
          enableMenu: true,
          enableSorting: true,
        },
        // @todo:to be fixed gracefully on BE and BE by renaming displayName in api and in FE hardcoded ENUM TABLE_COLUMNS_DISPLAYNAME
        // https://linear.app/peoplemesh/issue/ENG-15933/issueor-ag-gird-ratings-are-showing-error-for-some-columns
        // headerValueGetter: () => col.displayName,
        headerValueGetter: () => colProps.displayName?.replace(/QnA/i, 'Q&A'),
        ...getColumnDefinitionByType(colProps),
        ...additionalProps,
      };
    });

    setColumnDefs(newColumnDefs);
  }, [columns]);

  useEffect(() => {
    if (isFetching) {
      agGridRef.current?.api?.setGridOption('loading', true);
    } else {
      agGridRef.current?.api?.setGridOption('loading', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agGridRef.current, isFetching]);

  return (
    <PaperWithShadow p={4} mb={1} mt={checkNewNavEnabled() ? 0 : 3} position="relative">
      <Box mb={2} justifyContent="space-between" alignItems="center">
        <AgGridSearchBar gridRef={agGridRef} />
      </Box>
      <Box className="review review-report-grid">
        <AgGridTable
          key={gridStatePersistenceKey} // we want to re-initialize on key change
          gridStatePersistenceKey={gridStatePersistenceKey}
          ref={agGridRef}
          rowData={rows}
          columnDefs={columnDefs}
          domLayout="autoHeight"
          pagination
          paginationPageSize={10}
          headerHeight={36}
          rowHeight={56}
          kebabMenuIsRowSpecific={false}
          containerAutoHeight
          rowStyle={{ cursor: 'default' }}
        />
      </Box>
    </PaperWithShadow>
  );
}

export { ReviewDetailedReportGrid };
